import { useState, type FormEvent } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import {
  UniversalPage,
  UniversalLoadingState,
  Button,
  Input,
  Card,
  FormField,
  MoneyCell,
  DateCell,
  StatusBadge,
  NotificationCard,
  DateRangeField,
} from '@afrilink/ui';
import { siteConfig } from '@afrilink/config';
import {
  statementService,
  type CustomerStatement,
  type StatementTransaction,
} from '@/domains/billing';

/**
 * Statements page — customer self-service.
 *
 * Customer picks a date range and a filter, hits "Generate", we render the
 * statement on screen. PDF download arrives in a follow-up (the lifted
 * statementPDFService.ts is large enough to be its own PR).
 */
export default function StatementsPage() {
  const [fromDate, setFromDate] = useState(initialFromDate());
  const [toDate, setToDate] = useState(initialToDate());
  const [filter, setFilter] = useState<'all' | 'outstanding_only'>('all');

  const [statement, setStatement] = useState<CustomerStatement | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onGenerate(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!fromDate || !toDate) {
      setError('Pick a date range.');
      return;
    }
    if (fromDate > toDate) {
      setError('"From" date must be before "To" date.');
      return;
    }

    setLoading(true);
    setStatement(null);
    try {
      const s = await statementService.generate(fromDate, toDate, filter);
      setStatement(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not generate statement.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <UniversalPage
      title="Statements"
      description="Generate a statement of your account for any period."
      breadcrumbs={[
        { label: 'Home', href: '/app' },
        { label: 'Billing', href: '/app/billing' },
        { label: 'Statements' },
      ]}
    >
      <Card interactive={false}>
        <form
          onSubmit={onGenerate}
          className="flex flex-col gap-4 p-6 sm:flex-row sm:items-end"
        >
          {/*
            DateRangeField, not two bare `Input type="date"`. This is the LAST
            hand-rolled from/to filter pair in the portal — the component's own
            header named this page as one of three that "should migrate onto
            this" when it shipped on 2026-08-09, and none of them had.

            It also validates from <= to and SAYS so, where the pair here only
            clamped via min/max — a control that quietly rewrites what you typed
            is worse than one that tells you it is wrong.
          */}
          <DateRangeField
            className="flex-1"
            idPrefix="statement"
            from={fromDate}
            to={toDate}
            onChange={(next) => {
              setFromDate(next.from ?? '');
              setToDate(next.to ?? '');
            }}
          />
          <FormField label="Show" htmlFor="filter" className="flex-1">
            <select
              id="filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value as 'all' | 'outstanding_only')}
              disabled={loading}
              className="h-10 w-full rounded border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              <option value="all">All transactions</option>
              <option value="outstanding_only">Outstanding only</option>
            </select>
          </FormField>
          <Button type="submit" disabled={loading} loading={loading} className="shrink-0">
            {loading ? 'Generating…' : 'Generate'}
          </Button>
        </form>
      </Card>

      {error && <NotificationCard tone="danger" title="Could not generate statement" body={error} />}

      {loading && (
        <Card interactive={false} className="p-12">
          <UniversalLoadingState variant="spinner" caption="Compiling transactions…" />
        </Card>
      )}

      {statement && !loading && <StatementBody statement={statement} />}

      {!statement && !loading && !error && (
        <Card interactive={false} className="p-12 text-center">
          <FileText className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Pick a period and click Generate to view your statement.
          </p>
        </Card>
      )}
    </UniversalPage>
  );
}

function StatementBody({ statement }: { statement: CustomerStatement }) {
  return (
    <div className="space-y-6">
      <Card interactive={false} className="p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-navy-primary">Statement of account</h2>
            <p className="mt-1 text-sm text-foreground-soft">
              {statement.customerName}
              {statement.customerVatNumber && ` · VAT: ${statement.customerVatNumber}`}
            </p>
            <p className="mt-1 text-xs text-muted-foreground inline-flex items-center gap-1">
              Period: <DateCell value={statement.fromDate} format="long" /> →{' '}
              <DateCell value={statement.toDate} format="long" />
              {statement.filter === 'outstanding_only' ? ' · Outstanding only' : ''}
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-muted-foreground">Generated</p>
            <p className="text-sm">
              <DateCell value={statement.statementDate} format="long" />
            </p>
          </div>
        </div>

        <dl className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label="Opening" value={statement.openingBalanceZAR} />
          <Stat label="Invoiced" value={statement.totalInvoicedZAR} />
          <Stat label="Paid" value={-statement.totalPaidZAR} />
          <Stat label="Closing" value={statement.closingBalanceZAR} highlight />
        </dl>
      </Card>

      <Card interactive={false}>
        <header className="border-b border-border px-6 py-3">
          <h3 className="text-sm font-semibold text-navy-primary">Transactions</h3>
        </header>
        {statement.transactions.length === 0 ? (
          <p className="px-6 py-12 text-center text-sm text-muted-foreground">
            No transactions in this period.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-card-subtle text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Type</th>
                  <th className="px-4 py-2">Reference</th>
                  <th className="px-4 py-2">Description</th>
                  <th className="px-4 py-2 text-right">Debit</th>
                  <th className="px-4 py-2 text-right">Credit</th>
                  <th className="px-4 py-2 text-right">Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {statement.transactions.map((t, i) => (
                  <TransactionRow key={i} t={t} />
                ))}
              </tbody>
              <tfoot className="border-t-2 border-input">
                <tr>
                  <td colSpan={4} className="px-4 py-3 text-right text-sm font-semibold text-navy-primary">
                    Closing balance
                  </td>
                  <td />
                  <td />
                  <td className="px-4 py-3 text-right">
                    <MoneyCell value={statement.closingBalanceZAR} className="text-base" />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      <Card interactive={false}>
        <header className="border-b border-border px-6 py-3">
          <h3 className="text-sm font-semibold text-navy-primary">Aging of outstanding amounts</h3>
          <p className="mt-0.5 text-xs text-muted-foreground inline-flex items-center gap-1">
            Days past due as of <DateCell value={statement.toDate} format="long" />.
          </p>
        </header>
        <div className="grid grid-cols-2 gap-4 p-6 sm:grid-cols-3 lg:grid-cols-7">
          <AgingCell label="Current"     value={statement.aging.currentZAR} tone="active" />
          <AgingCell label="1–30 days"   value={statement.aging.days30ZAR}  tone="info" />
          <AgingCell label="31–60 days"  value={statement.aging.days60ZAR}  tone="pending" />
          <AgingCell label="61–90 days"  value={statement.aging.days90ZAR}  tone="pending" />
          <AgingCell label="91–120 days" value={statement.aging.days120ZAR} tone="overdue" />
          <AgingCell label="120+ days"   value={statement.aging.over120ZAR} tone="overdue" />
          <AgingCell label="Total"       value={statement.aging.totalZAR}   highlight />
        </div>
      </Card>

      {!siteConfig.vatRegistered && (
        <p className="text-xs text-muted-foreground">
          {siteConfig.legalName} is not registered for VAT. Amounts shown are not subject to VAT.
        </p>
      )}
    </div>
  );
}

function TransactionRow({ t }: { t: StatementTransaction }) {
  const typeLabel =
    t.kind === 'invoice' ? 'Invoice' : t.kind === 'payment' ? 'Payment' : 'Credit note';
  return (
    <tr className="hover:bg-card-subtle">
      <td className="whitespace-nowrap px-4 py-3 text-foreground-soft">
        <DateCell value={t.date} format="long" />
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-foreground-soft">{typeLabel}</td>
      <td className="whitespace-nowrap px-4 py-3 text-xs tabular-nums text-foreground-soft">
        {t.reference}
      </td>
      <td className="px-4 py-3 text-foreground-soft">{t.description}</td>
      <td className="whitespace-nowrap px-4 py-3 text-right">
        {t.debitZAR > 0 ? <MoneyCell value={t.debitZAR} /> : null}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-right">
        {t.creditZAR > 0 ? <MoneyCell value={t.creditZAR} /> : null}
      </td>
      <td className="whitespace-nowrap px-4 py-3 text-right">
        <MoneyCell value={t.balanceZAR} />
      </td>
    </tr>
  );
}

function Stat({
  label,
  value,
  highlight,
}: {
  label: string;
  value: number;
  highlight?: boolean;
}) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-1">
        <MoneyCell
          value={value}
          className={highlight ? 'text-base font-bold text-navy-primary' : 'text-foreground-soft'}
        />
      </dd>
    </div>
  );
}

function AgingCell({
  label,
  value,
  tone,
  highlight,
}: {
  label: string;
  value: number;
  tone?: 'active' | 'info' | 'pending' | 'overdue';
  highlight?: boolean;
}) {
  if (highlight) {
    return (
      <div className="rounded border border-navy-primary bg-navy-primary p-3 text-background">
        <p className="text-xs text-background/80">{label}</p>
        <p className="mt-1 text-sm font-bold tabular-nums">
          {new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 2 }).format(value)}
        </p>
      </div>
    );
  }
  return (
    <div className="rounded border border-border bg-card p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="mt-1">
        <MoneyCell value={value} />
        {tone && <StatusBadge tone={tone} className="ml-2 align-middle">{toneLabel(tone)}</StatusBadge>}
      </div>
    </div>
  );
}

function toneLabel(tone: 'active' | 'info' | 'pending' | 'overdue'): string {
  return tone === 'overdue' ? 'late' : tone === 'pending' ? 'due' : tone === 'info' ? 'due soon' : 'OK';
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function initialFromDate(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  return d.toISOString().slice(0, 10);
}

function initialToDate(): string {
  return today();
}
